import { useEffect, useState, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Logo } from "@/components/Logo";
import { Clock, MapPin, Users, Volume2, PauseCircle, Loader2, ChevronDown, ArrowRight } from "lucide-react";
import {
  fetchDisplaySlots,
  fetchDisplayGroups,
  getDisplayToken,
  setDisplayToken,
} from "@/lib/api";

// Leave the live board once RO has cleared them at the counter.
const APPROVED_STATUSES = ["roadmin_cleared", "verified"];
const HIDDEN_STATUSES = [...APPROVED_STATUSES, "rejected", "draft"];
const BOARD_SIZE = 5;
const CALL_DESTINATION = "the registration counter";

const RED = "#B60F2A";

const speak = (text) => {
  if (typeof window === "undefined" || !window.speechSynthesis) return;
  const utter = new SpeechSynthesisUtterance(text);
  utter.lang = "en-IN";
  utter.rate = 0.95;
  window.speechSynthesis.speak(utter);
};

const announceToken = (tokenNumber) => {
  speak(`Token ${tokenNumber}, please go to ${CALL_DESTINATION}.`);
};

const candidateKey = (c) => c.rollNumber || c.applicationNumber;

export default function SlotDisplay() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [now, setNow] = useState(() => new Date());
  const [groups, setGroups] = useState([]);
  // Selected slot group — the board is always single-group now (mixing
  // groups made seat/token numbers ambiguous, since both restart at 1 per
  // group). Seeded from ?group= so a physical screen can be bookmarked to
  // one venue's queue without reselecting after every refresh.
  const [groupId, setGroupId] = useState(() => {
    const g = searchParams.get("group");
    return g ? Number(g) : null;
  });
  const [displayToken, setDisplayTokenState] = useState(() => {
    const fromUrl = (searchParams.get("token") || searchParams.get("display_token") || "").trim();
    if (fromUrl) {
      setDisplayToken(fromUrl);
      return fromUrl;
    }
    return getDisplayToken();
  });
  const [tokenDraft, setTokenDraft] = useState(() => getDisplayToken() || (searchParams.get("token") || searchParams.get("display_token") || ""));
  const [authError, setAuthError] = useState(() => !(getDisplayToken() || searchParams.get("token") || searchParams.get("display_token")));
  const [groupPickerOpen, setGroupPickerOpen] = useState(false);
  const [rows, setRows] = useState([]);
  // Tokens currently "In Counter". Normally up to 5; On Hold releases append
  // even when that pushes the board past 5.
  const [boardKeys, setBoardKeys] = useState([]);
  const boardReadyRef = useRef(false);
  const announcedRef = useRef(new Set());
  const prevOnHoldRef = useRef(new Set());
  const releasePriorityRef = useRef([]); // keys released from on_hold, FIFO
  // Tokens released from hold re-enter In Counter silently (no re-announcement).
  const skipAnnounceRef = useRef(new Set());

  const unlockDisplay = (raw) => {
    const next = (raw || "").trim();
    if (!next) return;
    setDisplayToken(next);
    setDisplayTokenState(next);
    setTokenDraft(next);
    setAuthError(false);
  };

  useEffect(() => {
    const params = {};
    if (groupId != null) params.group = String(groupId);
    if (displayToken) params.token = displayToken;
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, displayToken]);

  useEffect(() => {
    if (!displayToken) {
      setGroups([]);
      setAuthError(true);
      return;
    }
    let cancelled = false;
    fetchDisplayGroups(displayToken).then((res) => {
      if (cancelled) return;
      if (res.unauthorized) {
        setAuthError(true);
        setGroups([]);
        return;
      }
      setAuthError(false);
      setGroups(Array.isArray(res.data) ? res.data : []);
    });
    return () => { cancelled = true; };
  }, [displayToken]);

  useEffect(() => {
    // No group selected yet — don't fetch at all. Fetching unscoped here
    // (no ?group=) would pull every group's candidates and the board/
    // announcement logic below would process them in the background even
    // while the UI only shows the "select a group" placeholder.
    if (groupId == null || !displayToken || authError) {
      setRows([]);
      return;
    }
    const load = async () => {
      const res = await fetchDisplaySlots(groupId, displayToken);
      if (res.unauthorized) {
        setAuthError(true);
        setRows([]);
        return;
      }
      setRows(Array.isArray(res.data) ? res.data : []);
    };
    load();
    const iv = setInterval(load, 10000);
    return () => clearInterval(iv);
  }, [groupId, displayToken, authError]);

  // Switching groups must behave like a fresh load — otherwise boardReadyRef
  // stays true from the previous group, and the new group's entire initial
  // in-counter batch gets announced all at once (looks like "every token" is
  // being called, not just the ones actually entering the counter).
  useEffect(() => {
    setBoardKeys([]);
    boardReadyRef.current = false;
    announcedRef.current = new Set();
    prevOnHoldRef.current = new Set();
    releasePriorityRef.current = [];
    skipAnnounceRef.current = new Set();
  }, [groupId]);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 15 * 1000);
    return () => clearInterval(t);
  }, []);

  const selectedGroup = useMemo(
    () => groups.find((g) => g.slot_group_id === groupId) || null,
    [groups, groupId]
  );

  // One unified "your number" concept, always labeled Token on this board —
  // seat-mode groups already have their number at submission (seatInSlot);
  // token/walkin-mode groups only get one once actually checked in
  // (token_number), so uncheck-in candidates aren't shown at all — they're
  // not callable yet.
  const allCandidates = useMemo(() => {
    return rows
      .filter((r) => !HIDDEN_STATUSES.includes(r.status))
      .map((r) => {
        const slot = r.assigned_slot || {};
        const tokenNumber = slot.seatInSlot ?? (r.checked_in_at ? r.token_number : null);
        if (tokenNumber == null) return null;
        return {
          rollNumber: r.roll_number,
          applicationNumber: r.application_number,
          status: r.status,
          tokenNumber,
          venue: slot.venue,
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.tokenNumber - b.tokenNumber);
  }, [rows]);

  const byKey = useMemo(() => {
    const map = new Map();
    allCandidates.forEach((c) => map.set(candidateKey(c), c));
    return map;
  }, [allCandidates]);

  const onHold = useMemo(
    () => allCandidates.filter((c) => c.status === "on_hold").sort((a, b) => a.tokenNumber - b.tokenNumber),
    [allCandidates]
  );

  const queueEligible = useMemo(
    () => allCandidates.filter((c) => c.status !== "on_hold"),
    [allCandidates]
  );

  // Track on-hold transitions — released tokens jump straight into the
  // In Counter priority queue (silently, no re-announcement).
  useEffect(() => {
    const onHoldKeys = new Set(onHold.map(candidateKey));
    const prev = prevOnHoldRef.current;

    prev.forEach((key) => {
      if (!onHoldKeys.has(key)) {
        if (byKey.has(key)) {
          const c = byKey.get(key);
          if (c && c.status !== "on_hold" && !HIDDEN_STATUSES.includes(c.status)) {
            if (!releasePriorityRef.current.includes(key)) {
              releasePriorityRef.current = [...releasePriorityRef.current, key];
            }
            skipAnnounceRef.current.add(key);
          }
        }
      }
    });

    prevOnHoldRef.current = onHoldKeys;
  }, [onHold, byKey]);

  // Maintain In Counter board:
  // - drop approved / on-hold / rejected
  // - On Hold releases always append (can exceed BOARD_SIZE)
  // - otherwise fill up to BOARD_SIZE in ascending token order
  useEffect(() => {
    const eligibleKeys = new Set(queueEligible.map(candidateKey));
    const eligibleByToken = [...queueEligible].sort((a, b) => a.tokenNumber - b.tokenNumber);

    setBoardKeys((prev) => {
      let next = prev.filter((key) => eligibleKeys.has(key));

      prev.forEach((key) => {
        if (!next.includes(key)) announcedRef.current.delete(key);
      });

      const onBoard = new Set(next);
      const added = [];

      const stillPriority = [];
      for (const key of releasePriorityRef.current) {
        if (eligibleKeys.has(key) && !onBoard.has(key)) {
          added.push(key);
          onBoard.add(key);
        } else if (eligibleKeys.has(key) && onBoard.has(key)) {
          // already on board
        } else if (!eligibleKeys.has(key)) {
          // gone / re-held / approved — drop
        } else {
          stillPriority.push(key);
        }
      }
      releasePriorityRef.current = stillPriority;

      const afterReleases = next.length + added.length;
      if (afterReleases < BOARD_SIZE) {
        let need = BOARD_SIZE - afterReleases;
        for (const c of eligibleByToken) {
          if (need <= 0) break;
          const key = candidateKey(c);
          if (onBoard.has(key)) continue;
          added.push(key);
          onBoard.add(key);
          need -= 1;
        }
      }

      if (added.length === 0) {
        boardReadyRef.current = true;
        return [...next].sort((ka, kb) => {
          const a = byKey.get(ka)?.tokenNumber ?? Number.MAX_SAFE_INTEGER;
          const b = byKey.get(kb)?.tokenNumber ?? Number.MAX_SAFE_INTEGER;
          return a - b;
        });
      }

      const shouldAnnounce = boardReadyRef.current;
      added.forEach((key) => {
        const c = byKey.get(key);
        if (!c) return;
        const silent = skipAnnounceRef.current.has(key);
        skipAnnounceRef.current.delete(key);
        if (silent) {
          announcedRef.current.add(key);
          return;
        }
        if (shouldAnnounce && !announcedRef.current.has(key)) {
          announceToken(c.tokenNumber);
          announcedRef.current.add(key);
        } else if (!shouldAnnounce) {
          announcedRef.current.add(key);
        }
      });

      boardReadyRef.current = true;
      const merged = [...next, ...added];
      return merged.sort((ka, kb) => {
        const a = byKey.get(ka)?.tokenNumber ?? Number.MAX_SAFE_INTEGER;
        const b = byKey.get(kb)?.tokenNumber ?? Number.MAX_SAFE_INTEGER;
        return a - b;
      });
    });
  }, [queueEligible, byKey]);

  const boardCandidates = useMemo(() => {
    return boardKeys
      .map((key) => byKey.get(key))
      .filter(Boolean)
      .sort((a, b) => a.tokenNumber - b.tokenNumber);
  }, [boardKeys, byKey]);

  const upcomingTokens = useMemo(() => {
    const onBoard = new Set(boardKeys);
    return queueEligible
      .filter((c) => !onBoard.has(candidateKey(c)))
      .sort((a, b) => a.tokenNumber - b.tokenNumber);
  }, [queueEligible, boardKeys]);

  const venueLabel = selectedGroup?.venue || boardCandidates[0]?.venue || "—";

  const nowStr = now.toLocaleString("en-IN", {
    weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
  });

  // All In Counter cards share Card-1 (spotlight) style.
  // No `layout` prop — layout animations were shuffling cards across the grid
  // when the next token filled a vacancy; cards should just pack left→right.
  const CandidateCard = ({ c }) => (
    <motion.button
      type="button"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.2 }}
      onClick={() => announceToken(c.tokenNumber)}
      title={`Call token ${c.tokenNumber}`}
      className="relative rounded-xl p-3 text-center border w-full cursor-pointer transition hover:scale-[1.02] active:scale-[0.98] text-white border-transparent shadow-md"
      style={{ backgroundColor: RED }}
      data-testid={`token-card-${c.tokenNumber}`}
    >
      <span className="absolute -top-2 left-1/2 -translate-x-1/2 text-[8px] font-bold uppercase tracking-widest bg-white text-[#B60F2A] px-2 py-0.5 rounded-full inline-flex items-center gap-0.5 whitespace-nowrap shadow-sm">
        <ArrowRight className="h-2.5 w-2.5" /> Counter
      </span>
      <div className="text-3xl font-bold tabular-nums text-white">{c.tokenNumber}</div>
      <div className="text-[10px] mt-1.5 tabular-nums truncate text-white/80">
        {c.applicationNumber}
      </div>
    </motion.button>
  );

  if (authError || !displayToken) {
    return (
      <div className="min-h-screen bg-white text-[#111] flex flex-col items-center justify-center px-6" data-testid="slot-display-locked">
        <div className="bg-white border border-[#F0F0F0] rounded-xl p-3 mb-6">
          <Logo className="h-8" />
        </div>
        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold mb-2" style={{ color: RED }}>Admissions 2026</div>
        <h1 className="text-xl font-bold mb-2">Token Display Access</h1>
        <p className="text-sm text-[#6B7280] max-w-md text-center mb-6">
          Enter the venue display key, or open a bookmarked URL with <code className="text-xs">?token=…</code>
        </p>
        <form
          className="flex flex-col sm:flex-row gap-2 w-full max-w-md"
          onSubmit={(e) => {
            e.preventDefault();
            unlockDisplay(tokenDraft);
          }}
        >
          <input
            type="password"
            value={tokenDraft}
            onChange={(e) => setTokenDraft(e.target.value)}
            placeholder="Display access token"
            className="flex-1 border border-[#E5E7EB] rounded-xl px-3 py-2.5 text-sm"
            data-testid="display-token-input"
            autoComplete="off"
          />
          <button
            type="submit"
            className="rounded-xl px-4 py-2.5 text-sm font-semibold text-white"
            style={{ backgroundColor: RED }}
            data-testid="display-token-submit"
          >
            Unlock
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-[#111] flex flex-col" data-testid="slot-display">
      <div className="border-b border-[#F0F0F0] px-8 py-5 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="bg-white border border-[#F0F0F0] rounded-xl p-2">
            <Logo className="h-8" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold" style={{ color: RED }}>Admissions 2026</div>
            <div className="text-xl font-bold text-[#111]">Token Queue · Live Display</div>
          </div>
        </div>
        <div className="flex items-center gap-6 text-right">
          <div className="relative">
            <button
              type="button"
              onClick={() => setGroupPickerOpen((o) => !o)}
              className="flex items-center gap-2 border border-[#E5E7EB] rounded-xl px-3 py-2 text-sm font-medium text-[#111] hover:bg-[#F7F7F7]"
              data-testid="btn-group-select"
            >
              {selectedGroup ? `Slot Group #${selectedGroup.slot_group_id}` : "Select a group"}
              <ChevronDown className="h-3.5 w-3.5" />
            </button>
            {groupPickerOpen && (
              <div
                className="absolute right-0 z-20 mt-2 w-64 rounded-xl border border-[#E5E7EB] bg-white shadow-lg p-2 max-h-72 overflow-y-auto text-left"
                data-testid="group-select-panel"
              >
                {groups.length === 0 && (
                  <div className="text-xs text-[#9CA3AF] px-2 py-1.5">No active groups configured.</div>
                )}
                {groups.map((g) => (
                  <button
                    key={g.slot_group_id}
                    type="button"
                    onClick={() => { setGroupId(g.slot_group_id); setGroupPickerOpen(false); }}
                    className={`w-full text-left px-2 py-1.5 rounded-lg hover:bg-[#F7F7F7] text-sm ${g.slot_group_id === groupId ? "text-[#B60F2A] font-semibold" : "text-[#111]"}`}
                    data-testid={`group-option-${g.slot_group_id}`}
                  >
                    Slot Group #{g.slot_group_id}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#9CA3AF]">Now</div>
            <div className="text-xl font-semibold tabular-nums text-[#111]">{nowStr}</div>
          </div>
          <div className="h-10 w-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: RED }}>
            <Volume2 className="h-5 w-5 text-white" />
          </div>
        </div>
      </div>

      {groupId == null ? (
        <div className="flex-1 flex flex-col items-center justify-center text-[#9CA3AF] gap-3">
          <div className="text-sm">Select a slot group above to start the live queue.</div>
        </div>
      ) : rows.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-[#9CA3AF] gap-3">
          <Loader2 className="h-8 w-8 animate-spin" style={{ color: RED }} />
          <div className="text-sm">Waiting for submitted registrations…</div>
        </div>
      ) : (
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-[minmax(0,7fr)_minmax(0,3fr)] gap-6 p-8 min-h-0 bg-[#FAFAFA]">
          {/* Left 70%: In Counter + Upcoming */}
          <div className="rounded-3xl bg-white border border-[#F0F0F0] p-8 flex flex-col overflow-hidden relative shadow-sm min-h-0">
            <div className="absolute top-0 left-0 right-0 h-1.5" style={{ backgroundColor: RED }} />
            <div className="relative flex items-center justify-between shrink-0">
              <div>
                <div className="text-xs tracking-[0.2em] uppercase font-bold" style={{ color: RED }}>
                  {boardCandidates.length > 0 ? "Now Serving" : "Waiting for tokens"}
                </div>
              </div>
              <div className="text-right">
                <div className="text-[#9CA3AF] text-xs uppercase tracking-widest">Venue</div>
                <div className="text-lg font-semibold flex items-center gap-1 justify-end text-[#111]">
                  <MapPin className="h-4 w-4" style={{ color: RED }} /> {venueLabel}
                </div>
              </div>
            </div>

            <div className="mt-8 relative flex-1 min-h-0 flex flex-col overflow-hidden">
              <div className="text-[#9CA3AF] text-xs uppercase tracking-widest mb-3 shrink-0">
                In Counter · go to {CALL_DESTINATION}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 shrink-0 content-start items-start">
                <AnimatePresence initial={false} mode="popLayout">
                  {boardCandidates.map((c) => (
                    <CandidateCard key={candidateKey(c)} c={c} />
                  ))}
                </AnimatePresence>
                {boardCandidates.length === 0 && (
                  <div className="col-span-full text-[#9CA3AF] text-sm py-10 text-center">
                    No tokens in counter right now.
                  </div>
                )}
              </div>

              <div className="mt-6 flex-1 min-h-0 flex flex-col">
                <div className="text-[#9CA3AF] text-xs uppercase tracking-widest mb-2 shrink-0">Upcoming tokens</div>
                {upcomingTokens.length === 0 ? (
                  <div className="text-[#9CA3AF] text-sm py-2">No upcoming tokens.</div>
                ) : (
                  <div
                    className="flex-1 min-h-0 overflow-y-auto grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-2 content-start pr-1"
                    data-testid="upcoming-tokens-strip"
                  >
                    {upcomingTokens.map((c) => (
                        <button
                          key={candidateKey(c)}
                          type="button"
                          onClick={() => announceToken(c.tokenNumber)}
                          title={`Call token ${c.tokenNumber}`}
                          className="rounded-lg px-2 py-2.5 text-center border transition hover:scale-[1.03] active:scale-[0.98] bg-white text-[#B60F2A] border-[#B60F2A]"
                          data-testid={`upcoming-token-${c.tokenNumber}`}
                        >
                          <div className="text-base font-bold tabular-nums leading-none">{c.tokenNumber}</div>
                          <div className="text-[8px] mt-1 tabular-nums truncate text-[#9CA3AF]">
                            {c.applicationNumber}
                          </div>
                        </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="mt-4 pt-6 relative flex items-center justify-between border-t border-[#F0F0F0] shrink-0">
              <div className="flex items-center gap-2 text-[#6B7280] text-sm">
                <Users className="h-4 w-4" style={{ color: RED }} /> {boardCandidates.length} in counter
                {boardCandidates.length > BOARD_SIZE ? ` (incl. hold releases)` : ""}
              </div>
              <div className="text-xs uppercase tracking-widest font-bold" style={{ color: RED }}>Live</div>
            </div>
          </div>

          {/* Right 30%: On Hold */}
          <div className="flex flex-col gap-4 min-h-0">
            <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 flex-1 flex flex-col min-h-0 relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1.5 bg-amber-400" />
              <div className="text-xs uppercase tracking-widest font-bold flex items-start gap-2 shrink-0 text-amber-800 leading-snug">
                <PauseCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span>ON HOLD · {onHold.length}</span>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 overflow-y-auto content-start flex-1">
                {onHold.map((c) => (
                  <div
                    key={candidateKey(c)}
                    className="rounded-lg px-2 py-4 text-center border border-amber-300 bg-amber-200 text-amber-950 w-full min-h-[72px] flex flex-col items-center justify-center"
                    data-testid={`on-hold-token-${c.tokenNumber}`}
                  >
                    <div className="text-2xl font-bold tabular-nums text-amber-900 leading-none">{c.tokenNumber}</div>
                    <div className="text-[8px] mt-1.5 tabular-nums truncate max-w-full text-amber-800/70">
                      {c.applicationNumber}
                    </div>
                    <div className="text-[8px] mt-1 font-semibold uppercase tracking-widest text-amber-700/80">On Hold</div>
                  </div>
                ))}
                {onHold.length === 0 && (
                  <div className="col-span-2 text-amber-700/60 text-sm py-6 text-center">No tokens waiting.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="border-t border-[#F0F0F0] px-8 py-4 flex items-center justify-between">
        <div className="text-xs text-[#9CA3AF] uppercase tracking-widest">Mahindra University · Verification Counter</div>
        <div className="text-xs text-[#9CA3AF] flex items-center gap-1"><Clock className="h-3 w-3" /> Auto-refreshing every 10s</div>
      </div>
    </div>
  );
}
